/*
  Cheerful Buddha purchase options (Phase 1, Backend agent).
  Behaviour for snippets/cb-purchase-options.liquid: custom element <cb-purchase-options>.

  - Keeps <input name="selling_plan"> in step with the chosen option (empty = one-time purchase).
    The snippet renders that input disabled, so it only posts once this script is running; a page
    whose script failed therefore adds a one-time purchase (with JavaScript off, a <noscript>
    select takes over).
    When it changes the value itself (variant change, "Subscribe from" link) it fires a bubbling
    `change` on that input, so form-level listeners such as the section's button label re-read it.
  - Shows the frequency panel for the chosen subscribe option and updates prices and savings from
    the per-variant data Shopify rendered into the snippet.
  - Shows the subscription perks (rendered separately with part: 'perks', [data-cb-po-perks] with
    the same data-section-id) only while a subscribe option is chosen, and switches the delivery
    line ([data-cb-po-ship]) between its subscription and one-time wording, and hides the section's
    one-off-only lines ([data-cb-po-once-only], same data-section-id) while a subscribe option is chosen.
  - Listens for `cb:variant-change` ({detail: {variant, sectionId}}) from the product section and
    hides plans the new variant cannot be bought with.
  - Dispatches `cb:plan-change` (bubbles) whenever the choice or price changes, so the section can
    update its button label. detail: {sectionId, productId, variantId, sellingPlanId,
    isSubscription, price, compareAtPrice, priceText, compareAtPriceText, savePercent, saveText}.
    The same object is available as element.state, and the form gets data-cb-po-mode.
  - Any element with data-cb-po-pick="subscription" (or "onetime") selects that option on click,
    and so does the `cb:select-purchase-option` event ({detail: {option: 'subscribe'}}) sent by
    the product page lifestyle band.
  Prices are formatted by Shopify (Liquid money filter), so no money formatting happens here.
*/
(() => {
  if (!('customElements' in window) || window.customElements.get('cb-purchase-options')) return;

  const VARIANT_EVENT = 'cb:variant-change';
  const PLAN_EVENT = 'cb:plan-change';

  const all = (root, selector) => Array.from(root.querySelectorAll(selector));
  const setText = (el, text) => {
    if (el && typeof text === 'string' && el.textContent !== text) el.textContent = text;
  };

  class CbPurchaseOptions extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onFormActivity = this.onFormActivity.bind(this);
      this.ready = false;
    }

    connectedCallback() {
      if (this.ready) return;
      const dataEl = this.querySelector('script[data-cb-po-data]');
      if (!dataEl) {
        // Children not parsed yet; try again once the document is ready.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
        }
        return;
      }
      this.ready = true;

      try {
        this.data = JSON.parse(dataEl.textContent || '{}');
      } catch (error) {
        this.data = {};
      }
      if (!this.data.variants) this.data.variants = {};

      this.variantId = this.dataset.variantId || '';
      this.input = this.querySelector('input[data-cb-po-input]');
      if (this.input) this.input.disabled = false;
      this.form =
        this.closest('form') || (this.dataset.formId ? document.getElementById(this.dataset.formId) : null);
      // The product section may already have chosen another size (a card's sold-out size link opens on
      // it, a restored ?variant=) and announced it before this deferred script listened: start from the
      // form's own id, so the rows show that size's prices (CRO, 9 Oct 2026).
      const idField = this.form ? this.form.querySelector('[name="id"]') : null;
      const formVariant = idField && idField.value ? String(idField.value) : '';
      if (formVariant && formVariant !== this.variantId && this.data.variants[formVariant]) {
        this.variantId = formVariant;
        this.dataset.variantId = formVariant;
      }
      // The starting choice is the default intent: one-time purchase unless Theme settings >
      // "Pre-select subscription" is on (data-default="subscription"), when Subscribe stays the intent
      // even if the first variant shown has no plans. A shopper's own choice replaces it.
      const checked = this.querySelector('input[data-cb-po-type]:checked');
      const firstSubscribe = this.querySelector('input[data-cb-po-type="subscription"]');
      if (checked && checked.dataset.cbPoType === 'subscription') this.preferred = checked.value;
      else if (this.dataset.default === 'onetime') this.preferred = checked ? checked.value : null;
      else this.preferred = firstSubscribe ? firstSubscribe.value : checked ? checked.value : null;

      this.addEventListener('change', this.onChange);
      document.addEventListener(VARIANT_EVENT, this.onVariantChange, true);
      if (this.form) {
        this.form.addEventListener('change', this.onFormActivity);
        this.form.addEventListener('input', this.onFormActivity);
        this.form.addEventListener('click', this.onFormActivity);
      }

      this.update({ emit: false });
      // The input was disabled until now: let form-level listeners (the section's button label,
      // which may have read the form before this script ran) read it again.
      if (this.input) this.input.dispatchEvent(new Event('change', { bubbles: true }));

      // Tell listeners attached by other deferred scripts about the starting state.
      const announce = () => window.setTimeout(() => this.emit('init'), 0);
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', announce, { once: true });
      } else {
        announce();
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener(VARIANT_EVENT, this.onVariantChange, true);
      if (this.form) {
        this.form.removeEventListener('change', this.onFormActivity);
        this.form.removeEventListener('input', this.onFormActivity);
        this.form.removeEventListener('click', this.onFormActivity);
      }
      this.ready = false;
    }

    /** Current choice, also sent as the cb:plan-change detail. */
    get state() {
      const type = this.querySelector('input[data-cb-po-type]:checked');
      const variant = this.variantData() || {};
      const planId = this.input ? this.input.value : '';
      const isSubscription = !!(type && type.dataset.cbPoType === 'subscription' && planId);
      const allocation = isSubscription && variant.plans ? variant.plans[planId] : null;
      const source = allocation || variant;
      return {
        sectionId: this.dataset.sectionId || null,
        productId: this.dataset.productId || null,
        variantId: this.variantId || null,
        sellingPlanId: isSubscription ? planId : null,
        isSubscription,
        price: typeof source.price === 'number' ? source.price : null,
        compareAtPrice: source.compare > 0 ? source.compare : null,
        priceText: source.priceText || '',
        compareAtPriceText: source.compare > 0 ? source.compareText || '' : '',
        savePercent: allocation ? allocation.save || 0 : 0,
        saveText: allocation ? allocation.saveText || '' : '',
      };
    }

    variantData() {
      return this.data.variants[this.variantId] || null;
    }

    typeRadios() {
      return all(this, 'input[data-cb-po-type]');
    }

    onChange(event) {
      const target = event.target;
      if (event.type !== 'change' || !(target instanceof HTMLInputElement)) return;
      if (!target.matches('[data-cb-po-type], [data-cb-po-plan]')) return;
      if (target.matches('[data-cb-po-type]')) this.preferred = target.value;
      this.update();
    }

    onFormActivity(event) {
      // Quantity changes (typed, or set by the section's stepper buttons) move the delivery line.
      if (event.type === 'click') {
        window.setTimeout(() => this.renderShipping(), 0);
      } else if (event.target && event.target.name === 'quantity') {
        this.renderShipping();
      }
    }

    onVariantChange(event) {
      const detail = event.detail || {};
      const ownSection = this.dataset.sectionId;
      if (detail.sectionId != null && ownSection && String(detail.sectionId) !== ownSection) return;
      if (detail.sectionId == null && event.target instanceof Element) {
        const related = event.target.contains(this) || (this.form && this.form.contains(event.target));
        if (!related) return;
      }
      const variant = detail.variant;
      if (!variant || variant.id == null) return;
      const id = String(variant.id);
      if (!this.data.variants[id]) return;
      this.variantId = id;
      this.dataset.variantId = id;
      this.update();
    }

    /** Select the subscribe or one-time option, for example from a "Subscribe from" link. */
    select(kind) {
      const wanted = kind === 'onetime' ? 'onetime' : 'subscription';
      const radio = this.typeRadios().find((r) => !r.disabled && r.dataset.cbPoType === wanted);
      if (!radio) return false;
      radio.checked = true;
      this.preferred = radio.value;
      this.update();
      return true;
    }

    update({ emit = true } = {}) {
      const variant = this.variantData();
      if (variant) this.applyAvailability(variant);

      const enabled = this.typeRadios().filter((r) => !r.disabled);
      const type =
        enabled.find((r) => r.value === this.preferred) ||
        enabled.find((r) => r.checked) ||
        enabled.find((r) => r.dataset.cbPoType === 'subscription') ||
        enabled[0] ||
        null;
      if (type && !type.checked) type.checked = true;

      const isSubscription = !!type && type.dataset.cbPoType === 'subscription';
      const chosenGroup = isSubscription ? type.dataset.cbPoGroupRadio : null;
      let planId = '';

      all(this, '[data-cb-po-group]').forEach((item) => {
        const plan = this.groupPlan(item);
        const selected = isSubscription && item.dataset.cbPoGroup === chosenGroup;
        const box = item.querySelector('[data-cb-po-box]');
        if (box) box.hidden = !selected;
        if (selected && plan) planId = plan.value;
        if (variant) this.renderGroup(item, plan, variant);
      });
      if (variant) this.renderOnce(variant);
      const subscribed = !!(isSubscription && planId);
      this.perks().forEach((line) => {
        line.hidden = !subscribed;
      });
      // Lines for one-off orders only (the section's Delivery line "Hide while Subscribe & save is
      // chosen") hide while Subscribe is chosen. The welcome offer doesn't use it: CHEERS25 works on a
      // first subscription order too (James, 14 Oct 2026, RULES 114).
      this.perks('[data-cb-po-once-only]').forEach((line) => {
        line.hidden = subscribed;
      });
      // The delivery line: the subscription wording ("after your saving") only while Subscribe is
      // chosen, else the one-time delivery note (the line hides when there is none).
      this.perks('[data-cb-po-ship]').forEach((line) => {
        const sub = line.querySelector('[data-cb-po-ship-sub]');
        const once = line.querySelector('[data-cb-po-ship-once]');
        if (sub) sub.hidden = !subscribed;
        if (once) once.hidden = subscribed;
        line.hidden = !subscribed && !once;
      });
      if (this.input) {
        const next = isSubscription ? planId : '';
        if (this.input.value !== next) {
          this.input.value = next;
          // Form-level listeners (the section's button label) read selling_plan on change.
          if (emit) this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      this.renderShipping();

      if (this.form) this.form.setAttribute('data-cb-po-mode', isSubscription && planId ? 'subscription' : 'onetime');
      if (emit) this.emit('change');
    }

    applyAvailability(variant) {
      const plans = variant.plans || {};
      all(this, '[data-cb-po-group]').forEach((item) => {
        let any = false;
        all(item, 'input[data-cb-po-plan]').forEach((radio) => {
          const ok = Object.prototype.hasOwnProperty.call(plans, radio.value);
          radio.disabled = !ok;
          const wrap = radio.closest('[data-cb-po-plan-item]');
          if (wrap) wrap.hidden = !ok;
          if (ok) any = true;
        });
        item.hidden = !any;
        const radio = item.querySelector('input[data-cb-po-type]');
        if (radio) radio.disabled = !any;
      });
      const once = this.querySelector('[data-cb-po-once]');
      const onceRadio = once ? once.querySelector('input[data-cb-po-type]') : null;
      if (onceRadio) {
        once.hidden = !!variant.requiresPlan;
        onceRadio.disabled = !!variant.requiresPlan;
      }
    }

    /** The checked, available plan in a group (the first available one if none is checked). */
    groupPlan(item) {
      const plans = all(item, 'input[data-cb-po-plan]').filter((r) => !r.disabled);
      const plan = plans.find((r) => r.checked) || plans[0] || null;
      if (plan && !plan.checked) plan.checked = true;
      return plan;
    }

    renderGroup(item, plan, variant) {
      const plans = variant.plans || {};
      const allocation = plan ? plans[plan.value] : null;
      if (allocation) {
        setText(item.querySelector('[data-cb-po-amount]'), allocation.priceText);
        this.renderWas(item.querySelector('[data-cb-po-was]'), allocation);
        this.renderSave(item.querySelector('[data-cb-po-save]'), allocation);
      }
      // The same saving on every frequency shows once, on the option's badge, not on each chip.
      const radios = all(item, 'input[data-cb-po-plan]');
      const texts = radios.map((radio) => (plans[radio.value] ? plans[radio.value].saveText : '')).filter(Boolean);
      const same = texts.length > 1 && texts.every((text) => text === texts[0]);
      radios.forEach((radio) => {
        const chip = radio.parentElement ? radio.parentElement.querySelector('[data-cb-po-plan-save]') : null;
        if (!chip) return;
        if (same) {
          chip.hidden = true;
          return;
        }
        if (plans[radio.value]) this.renderSave(chip, plans[radio.value]);
      });
    }

    renderOnce(variant) {
      const once = this.querySelector('[data-cb-po-once]');
      if (!once) return;
      setText(once.querySelector('[data-cb-po-amount]'), variant.priceText);
      this.renderWas(once.querySelector('[data-cb-po-was]'), variant);
      // Sitewide sale (snippets/cb-sale.liquid): the block before the price, refilled for this size. saleText is in
      // the data only while the sale is live.
      const sale = once.querySelector('[data-cb-sale-block]');
      if (sale && variant.saleText && window.CBSale) window.CBSale.fill(sale, variant.priceText, variant.saleText);
    }

    renderWas(el, source) {
      if (!el) return;
      const show = source.compare > source.price && !!source.compareText;
      el.hidden = !show;
      if (show) setText(el, source.compareText);
      // The struck price is hidden from screen readers; this reads it with its label ("Bought separately" for a
      // bundle, "Was" for a single product's sale) when it has one.
      const sr = el.parentElement ? el.parentElement.querySelector('[data-cb-po-was-sr]') : null;
      if (sr) {
        const text = show && source.compareLabel ? `${source.compareLabel} ${source.compareText}` : '';
        sr.hidden = !text;
        setText(sr, text);
      }
    }

    renderSave(el, allocation) {
      if (!el) return;
      const show = allocation.save > 0 && !!allocation.saveText;
      el.hidden = !show;
      if (show) setText(el, allocation.saveText);
    }

    quantity() {
      if (!this.form) return 1;
      const field = this.form.elements.namedItem('quantity');
      const value = field && 'value' in field ? parseInt(field.value, 10) : NaN;
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    /** Perk lines (or, with a selector, the delivery line) rendered with part: 'perks' for this
        section and product. */
    perks(selector = '[data-cb-po-perks]') {
      const sectionId = this.dataset.sectionId || '';
      const productId = this.dataset.productId || '';
      return all(document, selector).filter(
        (block) => (block.dataset.sectionId || '') === sectionId && (block.dataset.productId || '') === productId
      );
    }

    renderShipping() {
      const many = this.quantity() >= 2;
      const lines = [this, ...this.perks()].flatMap((root) => all(root, '[data-cb-po-perk-ship]'));
      lines.forEach((line) => {
        const text = many ? line.dataset.multi : line.dataset.single;
        if (text) setText(line.querySelector('[data-cb-po-perk-text]') || line, text);
      });
    }

    emit(reason) {
      this.dispatchEvent(
        new CustomEvent(PLAN_EVENT, { bubbles: true, detail: Object.assign({ reason }, this.state) })
      );
    }
  }

  window.customElements.define('cb-purchase-options', CbPurchaseOptions);

  // "Subscribe from ..." calls to action elsewhere on the page pick an option, either through
  // data-cb-po-pick="subscription|onetime" or the `cb:select-purchase-option` event
  // ({detail: {option: 'subscribe'|'onetime', sectionId?}}) sent by sections/cb-pdp-lifestyle.liquid.
  const pick = (kind, sectionId) => {
    const wanted = /^(one|once)/i.test(String(kind || '')) ? 'onetime' : 'subscription';
    document.querySelectorAll('cb-purchase-options').forEach((el) => {
      if (sectionId && el.dataset.sectionId && el.dataset.sectionId !== String(sectionId)) return;
      if (typeof el.select === 'function') el.select(wanted);
    });
  };
  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-cb-po-pick]') : null;
    if (trigger) pick(trigger.getAttribute('data-cb-po-pick'));
  });
  document.addEventListener('cb:select-purchase-option', (event) => {
    const detail = event.detail || {};
    pick(detail.option, detail.sectionId);
  });
})();
